import {
  DELIVERABLE_PRIORITIES,
  DELIVERABLE_PRIORITY_LABELS,
  DELIVERABLE_STATUS_LABELS,
  DELIVERABLE_STATUSES,
  type DeliverablePriority,
  type DeliverableStatus,
} from "@dream/config/constants";
import { ACTIVITY_LABELS, type DeliverableLink, describeRecurrence } from "@dream/domain";
import {
  Button,
  ConfirmDialog,
  Dialog,
  Field,
  IconBolt,
  IconChevronDown,
  IconClock,
  IconClose,
  IconPlus,
  IconPublish,
  IconRepeat,
  IconSave,
  IconTrash,
  IconUserPlus,
  IconVideo,
  Input,
  MenuSelect,
  Skeleton,
  StatusBadge,
  Textarea,
  Toggle,
  useToast,
} from "@dream/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { type ReactNode, useEffect, useId, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { ApiError, api, unwrap } from "../api/client.ts";
import { useActivity, useDeliverable } from "../hooks/use-calendar.ts";
import { clientInviteEmails, formatInviteEmails } from "../lib/client-emails.ts";
import { isFigmaPresentationUrl } from "../lib/figma-url.ts";
import { nextWeekdayDate } from "../lib/schedule-copy.ts";
import { usePreferences } from "../preferences/provider.tsx";
import { TypePicker } from "./type-picker.tsx";

/**
 * Criação e edição de entregas.
 *
 * O formulário mostra só o essencial (título, tipo, cliente, quando — e o
 * status na edição). O restante fica atrás de "Mais opções": nada some, só
 * deixa de competir com a ação do momento. A recorrência ainda vira RRULE
 * RFC 5545; a API valida de novo.
 */

export type DialogState =
  | { mode: "create"; startsAt?: string; endsAt?: string; allDay?: boolean }
  | { mode: "edit"; deliverableId: string; occurrenceStart: string };

export interface DeliverableDialogProps {
  state: DialogState;
  clients: Array<{
    id: string;
    name: string;
    color: string;
    email?: string | null;
    emails?: Array<{ email: string; inviteCalendar?: boolean }>;
  }>;
  projects: Array<{ id: string; name: string; clientId: string }>;
  types: Array<{
    id: string;
    label: string;
    color: string;
    slug: string;
    icon: string | null;
  }>;
  team: Array<{ id: string; name: string; email?: string }>;
  timezone: string;
  defaults?: {
    clientId?: string;
    projectId?: string;
    typeId?: string;
  };
  onClose: () => void;
}

type Frequency = "none" | "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

interface FormValues {
  clientId: string;
  projectId: string;
  typeId: string;
  title: string;
  description: string;
  status: DeliverableStatus;
  priority: DeliverablePriority;
  assigneeId: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  isAllDay: boolean;
  visibleToClient: boolean;
  inviteClient: boolean;
  inviteTeam: boolean;
  frequency: Frequency;
  interval: number;
  publishDate: string;
  publishTime: string;
  videoUrl: string;
  links: DeliverableLink[];
}

function videoLinkFrom(links: DeliverableLink[]): string {
  const hit = links.find(
    (link) =>
      link.label.toLocaleLowerCase("pt-BR") === "videochamada" || /meet|zoom|call/i.test(link.url),
  );
  return hit?.url ?? "";
}

function mergeVideoLink(links: DeliverableLink[], videoUrl: string): DeliverableLink[] {
  const trimmed = videoUrl.trim();
  const without = links.filter((link) => link.label.toLocaleLowerCase("pt-BR") !== "videochamada");
  if (trimmed === "") return without.filter((link) => link.url.trim() !== "");
  return [...without, { label: "Videochamada", url: trimmed }].filter(
    (link) => link.url.trim() !== "",
  );
}
function toZonedDateTime(value: string | Date, timezone: string): DateTime {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value, { zone: timezone });
  }
  const fromIso = DateTime.fromISO(value, { zone: timezone });
  if (fromIso.isValid) return fromIso;
  return DateTime.fromJSDate(new Date(value), { zone: timezone });
}

function splitInstant(
  iso: string | Date | undefined,
  timezone: string,
): { date: string; time: string } {
  const fallback = DateTime.now().setZone(timezone).startOf("hour").plus({ hours: 1 });
  if (iso === undefined) {
    return { date: fallback.toFormat("yyyy-MM-dd"), time: fallback.toFormat("HH:mm") };
  }
  const dt = toZonedDateTime(iso, timezone);
  if (!dt.isValid) {
    return { date: fallback.toFormat("yyyy-MM-dd"), time: fallback.toFormat("HH:mm") };
  }
  return { date: dt.toFormat("yyyy-MM-dd"), time: dt.toFormat("HH:mm") };
}

function joinInstant(date: string, time: string, timezone: string, allDay: boolean): string {
  const dt = DateTime.fromFormat(`${date} ${allDay ? "00:00" : time}`, "yyyy-MM-dd HH:mm", {
    zone: timezone,
  });
  return dt.toUTC().toISO() ?? new Date().toISOString();
}

function buildRrule(frequency: Frequency, interval: number): string | null {
  if (frequency === "none") return null;
  return interval > 1 ? `FREQ=${frequency};INTERVAL=${interval}` : `FREQ=${frequency}`;
}

function parseFrequency(rrule: string | null | undefined): {
  frequency: Frequency;
  interval: number;
} {
  if (rrule === null || rrule === undefined) return { frequency: "none", interval: 1 };
  const freq = /FREQ=([A-Z]+)/.exec(rrule)?.[1];
  const interval = Number(/INTERVAL=(\d+)/.exec(rrule)?.[1] ?? 1);
  const valid: Frequency[] = ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"];
  return {
    frequency: valid.includes(freq as Frequency) ? (freq as Frequency) : "none",
    interval: Number.isFinite(interval) && interval > 0 ? interval : 1,
  };
}

/** Resumo do que está em Mais opções, para o campo não parecer vazio. */
function describeAdvanced(input: {
  assigneeName: string | undefined;
  priority: DeliverablePriority;
  status: DeliverableStatus;
  includeStatus: boolean;
  recurrence: string | null;
  hasPublishDate: boolean;
  linkCount: number;
  hasDescription: boolean;
  visibleToClient: boolean;
  inviteClient: boolean;
  inviteTeam: boolean;
}): string {
  const parts: string[] = [];
  if (input.includeStatus && input.status !== "planned") {
    parts.push(DELIVERABLE_STATUS_LABELS[input.status]);
  }
  if (input.assigneeName !== undefined) parts.push(input.assigneeName);
  if (input.priority !== "medium") parts.push(DELIVERABLE_PRIORITY_LABELS[input.priority]);
  if (input.recurrence !== null) parts.push(input.recurrence);
  if (input.hasPublishDate) parts.push("data de publicação");
  if (input.linkCount === 1) parts.push("1 link");
  if (input.linkCount > 1) parts.push(`${input.linkCount} links`);
  if (input.hasDescription) parts.push("descrição");
  if (!input.visibleToClient) parts.push("oculta do cliente");
  if (input.inviteClient) parts.push("convite ao cliente");
  if (input.inviteTeam) parts.push("convite à equipe");
  return parts.length === 0
    ? "Links, convites, status, prioridade e visibilidade"
    : parts.join(" · ");
}

export function DeliverableDialog({
  state,
  clients,
  projects,
  types,
  team,
  timezone,
  defaults,
  onClose,
}: DeliverableDialogProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { prefs } = usePreferences();
  const isEdit = state.mode === "edit";
  const visibleToggleId = useId();

  const detail = useDeliverable(isEdit ? state.deliverableId : null);
  const activity = useActivity(isEdit ? state.deliverableId : null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancelOccurrence, setConfirmCancelOccurrence] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [openRow, setOpenRow] = useState<
    null | "assignee" | "repeat" | "calendar" | "publish" | "video"
  >(null);

  const start = splitInstant(state.mode === "create" ? state.startsAt : undefined, timezone);
  const end = splitInstant(state.mode === "create" ? state.endsAt : undefined, timezone);

  const form = useForm<FormValues>({
    defaultValues: {
      clientId: defaults?.clientId ?? clients[0]?.id ?? "",
      projectId: defaults?.projectId ?? "",
      typeId: defaults?.typeId ?? types[0]?.id ?? "",
      title: "",
      description: "",
      status: "planned",
      priority: "medium",
      assigneeId: "",
      startDate: start.date,
      startTime: start.time,
      endDate: end.date,
      endTime:
        state.mode === "create" && state.endsAt !== undefined
          ? end.time
          : DateTime.fromFormat(start.time, "HH:mm").plus({ hours: 1 }).toFormat("HH:mm"),
      isAllDay: state.mode === "create" ? (state.allDay ?? false) : false,
      visibleToClient: true,
      inviteClient: true,
      inviteTeam: false,
      frequency: "none",
      interval: 1,
      publishDate: "",
      publishTime: "",
      videoUrl: prefs.defaultVideo === "custom" ? prefs.customMeetUrl : "",
      links: [],
    },
  });

  const { register, handleSubmit, watch, reset, formState, control, setValue, getValues } = form;

  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () =>
      unwrap(await api.api.me.get()) as {
        email: string;
        userId: string;
        scheduleInviteClient?: boolean;
        scheduleInviteTeam?: boolean;
      },
  });

  useEffect(() => {
    if (isEdit) return;
    const userId = me.data?.userId;
    if (userId === undefined) return;
    if (!team.some((member) => member.id === userId)) return;
    if (getValues("assigneeId") !== "") return;
    setValue("assigneeId", userId);
  }, [getValues, isEdit, me.data?.userId, setValue, team]);

  useEffect(() => {
    if (isEdit) return;
    if (me.data === undefined) return;
    setValue("inviteClient", me.data.scheduleInviteClient ?? true);
    setValue("inviteTeam", me.data.scheduleInviteTeam ?? false);
  }, [isEdit, me.data, setValue]);

  /**
   * Links da entrega.
   *
   * A validação real é do servidor, que rejeita qualquer esquema fora de
   * http/https — o portal do cliente renderiza estes links, então um
   * `javascript:` aqui seria vetor de XSS. O `type="url"` do input é só
   * conveniência.
   */
  const linkFields = useFieldArray({ control, name: "links" });

  // Preenche o formulário quando o detalhe da entrega carrega.
  useEffect(() => {
    if (!isEdit || detail.data === undefined) return;

    const d = detail.data.deliverable;
    const startParts = splitInstant(d.startsAt, timezone);
    const endParts = splitInstant(d.endsAt, timezone);
    const publishParts =
      d.publishAt === null ? { date: "", time: "" } : splitInstant(d.publishAt, timezone);
    const recurrence = parseFrequency(detail.data.recurrence?.rrule);

    reset({
      clientId: d.clientId,
      projectId: d.projectId ?? "",
      typeId: d.typeId,
      title: d.title,
      description: d.description ?? "",
      status: d.status,
      priority: d.priority,
      assigneeId: d.assigneeId ?? "",
      startDate: startParts.date,
      startTime: startParts.time,
      endDate: endParts.date,
      endTime: endParts.time,
      isAllDay: d.isAllDay,
      visibleToClient: d.visibleToClient,
      inviteClient: "inviteClient" in d ? Boolean(d.inviteClient) : true,
      inviteTeam: "inviteTeam" in d ? Boolean(d.inviteTeam) : false,
      frequency: recurrence.frequency,
      interval: recurrence.interval,
      publishDate: publishParts.date,
      publishTime: publishParts.time,
      videoUrl: videoLinkFrom(d.links),
      links: d.links,
    });
  }, [isEdit, detail.data, reset, timezone]);

  const isAllDay = watch("isAllDay");
  const clientId = watch("clientId");
  const typeId = watch("typeId");
  const startDate = watch("startDate");
  const frequency = watch("frequency");
  const interval = watch("interval");
  const assigneeId = watch("assigneeId");
  const priority = watch("priority");
  const status = watch("status");
  const publishDate = watch("publishDate");
  const videoUrl = watch("videoUrl");
  const description = watch("description");
  const visibleToClient = watch("visibleToClient");
  const inviteClient = watch("inviteClient");
  const inviteTeam = watch("inviteTeam");
  const links = watch("links");

  const availableProjects = useMemo(
    () => projects.filter((p) => p.clientId === clientId),
    [projects, clientId],
  );
  const selectedClient = useMemo(
    () => clients.find((client) => client.id === clientId),
    [clients, clientId],
  );
  const selectedType = useMemo(() => types.find((type) => type.id === typeId), [types, typeId]);

  const recurrenceDescription = useMemo(() => {
    const rrule = buildRrule(frequency, Number(interval));
    if (rrule === null) return null;
    try {
      return describeRecurrence(rrule, timezone, new Date());
    } catch {
      return null;
    }
  }, [frequency, interval, timezone]);

  const isRecurringSeries = detail.data?.recurrence != null;
  const version = (detail.data?.deliverable as { version?: number } | undefined)?.version ?? 1;

  const advancedHint = useMemo(
    () =>
      describeAdvanced({
        assigneeName: team.find((member) => member.id === assigneeId)?.name,
        priority,
        status,
        includeStatus: !isEdit,
        recurrence: recurrenceDescription,
        hasPublishDate: publishDate !== "",
        linkCount: links.filter((link) => link.url.trim() !== "").length,
        hasDescription: description.trim() !== "",
        visibleToClient,
        inviteClient,
        inviteTeam,
      }),
    [
      assigneeId,
      description,
      inviteClient,
      inviteTeam,
      isEdit,
      links,
      priority,
      publishDate,
      recurrenceDescription,
      status,
      team,
      visibleToClient,
    ],
  );

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const startsAt = joinInstant(values.startDate, values.startTime, timezone, values.isAllDay);
      const endsAt = values.isAllDay
        ? (DateTime.fromISO(joinInstant(values.endDate, "00:00", timezone, true))
            .plus({ days: 1 })
            .toUTC()
            .toISO() ?? startsAt)
        : joinInstant(values.endDate, values.endTime, timezone, false);

      const publishAt =
        values.publishDate === ""
          ? null
          : joinInstant(values.publishDate, values.publishTime || "12:00", timezone, false);

      const recurrence = buildRrule(values.frequency, Number(values.interval));

      const payload = {
        title: values.title.trim(),
        description: values.description.trim() === "" ? null : values.description.trim(),
        status: values.status,
        priority: values.priority,
        assigneeId: values.assigneeId === "" ? null : values.assigneeId,
        projectId: values.projectId === "" ? null : values.projectId,
        typeId: values.typeId,
        startsAt,
        endsAt,
        isAllDay: values.isAllDay,
        timezone,
        publishAt,
        visibleToClient: values.visibleToClient,
        inviteClient: values.inviteClient,
        inviteTeam: values.inviteTeam,
        recurrence: recurrence === null ? null : { rrule: recurrence, timezone },
        // Descarta linhas em branco: uma URL vazia seria rejeitada com 422.
        links: mergeVideoLink(values.links, values.videoUrl),
      };

      if (isEdit) {
        return unwrap(
          await api.api.admin.deliverables({ id: state.deliverableId }).patch({
            version,
            ...payload,
          }),
        );
      }

      return unwrap(
        await api.api.admin.deliverables.post({
          clientId: values.clientId,
          ...payload,
        }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
      void queryClient.invalidateQueries({ queryKey: ["deliverable"] });
      toast.success(isEdit ? "Entrega atualizada" : "Entrega criada");
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.isVersionConflict) {
        toast.error(
          "A entrega foi alterada por outra pessoa",
          "Feche e reabra a entrega para ver a versão atual antes de salvar.",
        );
        return;
      }
      toast.error(
        "Não foi possível salvar",
        error instanceof ApiError ? error.message : "Tente novamente.",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!isEdit) return;
      return unwrap(
        await api.api.admin
          .deliverables({ id: state.deliverableId })
          .delete(undefined, { query: { version: String(version) } }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
      toast.success("Entrega excluída");
      setConfirmDelete(false);
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível excluir",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  const cancelOccurrenceMutation = useMutation({
    mutationFn: async () => {
      if (!isEdit) return;
      return unwrap(
        await api.api.admin
          .deliverables({ id: state.deliverableId })
          .occurrences.delete(undefined, {
            query: { occurrenceStart: state.occurrenceStart },
          }),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
      toast.success("Ocorrência cancelada", "As outras ocorrências da série continuam agendadas.");
      setConfirmCancelOccurrence(false);
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível cancelar a ocorrência",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      if (!isEdit) return;
      return unwrap(
        await api.api.admin.deliverables({ id: state.deliverableId }).duplicate.post({}),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
      toast.success("Entrega duplicada", "A cópia foi criada como Planejado.");
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível duplicar",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  const loading = isEdit && detail.isLoading;

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={isEdit ? "Editar entrega" : "Nova entrega"}
        description={undefined}
        size="lg"
        placement="right"
        footer={
          <>
            {isEdit && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Excluir"
                onClick={() => setConfirmDelete(true)}
                className="mr-auto border border-[#F99696] text-[#AB3434] hover:bg-[#FEEDED]"
              >
                <IconTrash className="size-4" />
              </Button>
            )}
            {isEdit && isRecurringSeries && (
              <Button variant="ghost" size="sm" onClick={() => setConfirmCancelOccurrence(true)}>
                Cancelar esta ocorrência
              </Button>
            )}
            {isEdit && (
              <Button
                variant="secondary"
                size="sm"
                className="hidden sm:inline-flex"
                onClick={() => {
                  const start = getValues("startDate");
                  const end = getValues("endDate");
                  const nextStart = DateTime.fromISO(start).plus({ days: 1 });
                  const nextEnd = DateTime.fromISO(end).plus({ days: 1 });
                  if (nextStart.isValid) setValue("startDate", nextStart.toFormat("yyyy-MM-dd"));
                  if (nextEnd.isValid) setValue("endDate", nextEnd.toFormat("yyyy-MM-dd"));
                }}
              >
                <IconClock className="size-4" />
                Reagendar
              </Button>
            )}
            <Button variant="secondary" className="w-full sm:w-auto" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="w-full sm:w-auto"
              loading={saveMutation.isPending}
              disabled={!isEdit && clients.length === 0}
              onClick={() => void handleSubmit((values) => saveMutation.mutate(values))()}
            >
              <IconSave className="size-4" />
              {isEdit ? "Salvar" : "Criar entrega"}
            </Button>
          </>
        }
      >
        {loading ? (
          <div className="space-y-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit((values) => saveMutation.mutate(values))}
            className="space-y-4"
            noValidate
          >
            {!isEdit && clients.length === 0 && (
              <div
                role="status"
                className="rounded-[var(--radius-md)] border border-[var(--dream-info-border)] bg-[var(--dream-info-bg)] px-3 py-3 text-[0.8125rem] leading-5 text-[var(--dream-info-fg)]"
              >
                <p className="font-semibold">Cadastre um cliente primeiro</p>
                <p className="mt-1">
                  Toda entrega pertence a um cliente. Sem esse cadastro, o calendário não tem para
                  quem registrar.
                </p>
                <Link
                  to="/clientes"
                  className="mt-2 inline-flex font-semibold underline underline-offset-2"
                  onClick={onClose}
                >
                  Ir para clientes
                </Link>
              </div>
            )}

            <Field label="Título" htmlFor="title" required error={formState.errors.title?.message}>
              <Input
                id="title"
                autoFocus={!isEdit}
                placeholder={
                  selectedType === undefined
                    ? "O que precisa ser entregue?"
                    : `Ex.: ${selectedType.label} — tesoura nova`
                }
                invalid={formState.errors.title !== undefined}
                {...register("title", {
                  required: "Informe o título da entrega",
                  maxLength: {
                    value: 200,
                    message: "Máximo de 200 caracteres",
                  },
                })}
              />
            </Field>

            {isEdit && (
              <Field label="Status" htmlFor="status">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <MenuSelect
                      id="status"
                      aria-label="Status"
                      value={field.value}
                      onChange={field.onChange}
                      options={DELIVERABLE_STATUSES.map((item) => ({
                        value: item,
                        label: DELIVERABLE_STATUS_LABELS[item],
                      }))}
                    />
                  )}
                />
              </Field>
            )}

            <Field label="Tipo" htmlFor="typeId" required>
              <Controller
                name="typeId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TypePicker
                    id="typeId"
                    types={types}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Cliente"
                htmlFor="clientId"
                required
                hint={
                  selectedClient === undefined
                    ? "Sem e-mail — cadastre em Clientes para o convite da Agenda."
                    : clientInviteEmails(selectedClient).length === 0
                      ? "Sem e-mail — cadastre em Clientes para o convite da Agenda."
                      : formatInviteEmails(clientInviteEmails(selectedClient))
                }
              >
                <Controller
                  name="clientId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <MenuSelect
                      id="clientId"
                      aria-label="Cliente"
                      value={field.value}
                      onChange={(next) => {
                        field.onChange(next);
                        setValue("projectId", "");
                      }}
                      options={
                        clients.length === 0
                          ? [{ value: "", label: "Nenhum cliente cadastrado" }]
                          : clients.map((client) => ({
                              value: client.id,
                              label: client.name,
                            }))
                      }
                    />
                  )}
                />
              </Field>

              <Field label="Projeto ou campanha" htmlFor="projectId" hint="Opcional">
                <Controller
                  name="projectId"
                  control={control}
                  render={({ field }) => (
                    <MenuSelect
                      id="projectId"
                      aria-label="Projeto ou campanha"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: "", label: "Sem projeto" },
                        ...availableProjects.map((project) => ({
                          value: project.id,
                          label: project.name,
                        })),
                      ]}
                    />
                  )}
                />
              </Field>
            </div>

            <div className="space-y-3">
              <label className="flex cursor-pointer items-center gap-2 text-[0.8125rem] font-medium text-[var(--color-ink-secondary)]">
                <input
                  type="checkbox"
                  className="size-4 rounded border-[var(--color-line-strong)] text-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                  {...register("isAllDay")}
                />
                Dia inteiro
              </label>

              <div className={isAllDay ? "grid gap-3" : "grid grid-cols-3 gap-2"}>
                <div className="dream-mini-window p-2">
                  <p className="mb-1 text-[11px] font-semibold text-[var(--color-ink-muted)]">Em</p>
                  <Input
                    id="startDate"
                    type="date"
                    aria-label="Início"
                    className="h-9 border-0 bg-transparent px-0 shadow-none"
                    {...register("startDate", {
                      required: true,
                      onChange: (event) => {
                        if (!isAllDay) setValue("endDate", event.target.value);
                      },
                    })}
                  />
                </div>
                {isAllDay ? (
                  <div className="dream-mini-window p-2">
                    <p className="mb-1 text-[11px] font-semibold text-[var(--color-ink-muted)]">
                      Até
                    </p>
                    <Input
                      id="endDate"
                      type="date"
                      aria-label="Fim"
                      className="h-9 border-0 bg-transparent px-0 shadow-none"
                      {...register("endDate", { required: true })}
                    />
                  </div>
                ) : (
                  <>
                    <div className="dream-mini-window p-2">
                      <p className="mb-1 text-[11px] font-semibold text-[var(--color-ink-muted)]">
                        Das
                      </p>
                      <Input
                        type="time"
                        aria-label="Hora de início"
                        className="h-9 border-0 bg-transparent px-0 shadow-none"
                        {...register("startTime")}
                      />
                    </div>
                    <div className="dream-mini-window p-2">
                      <p className="mb-1 text-[11px] font-semibold text-[var(--color-ink-muted)]">
                        Até
                      </p>
                      <Input
                        type="time"
                        aria-label="Hora de fim"
                        className="h-9 border-0 bg-transparent px-0 shadow-none"
                        {...register("endTime")}
                      />
                    </div>
                    <input
                      type="hidden"
                      id="endDate"
                      {...register("endDate", { required: true })}
                    />
                  </>
                )}
              </div>

              <p className="text-xs text-[var(--color-ink-subtle)]">Horários no fuso {timezone}.</p>

              <DateQuickPicks
                timezone={timezone}
                value={startDate}
                onPick={(date) => {
                  setValue("startDate", date);
                  if (!isAllDay) setValue("endDate", date);
                }}
              />

              <label
                htmlFor={visibleToggleId}
                className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-3 py-2.5"
              >
                <span className="min-w-0 text-[0.8125rem] leading-5">
                  <span className="font-medium text-[var(--color-ink)]">Cliente vê no portal</span>
                  <span className="mt-0.5 block text-[0.75rem] text-[var(--color-ink-muted)]">
                    Aparece no calendário público deste cliente.
                  </span>
                </span>
                <Controller
                  name="visibleToClient"
                  control={control}
                  render={({ field }) => (
                    <Toggle
                      aria-label="Visível para o cliente"
                      checked={field.value}
                      id={visibleToggleId}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </label>
            </div>

            <DrawerRow
              icon={<IconUserPlus className="size-4" />}
              label="Responsável"
              open={openRow === "assignee"}
              onToggle={() => setOpenRow((current) => (current === "assignee" ? null : "assignee"))}
              summary={team.find((member) => member.id === assigneeId)?.name ?? "Adicionar"}
            >
              <Field label="Responsável" htmlFor="assigneeId">
                <Controller
                  name="assigneeId"
                  control={control}
                  render={({ field }) => (
                    <MenuSelect
                      id="assigneeId"
                      aria-label="Responsável"
                      value={field.value}
                      onChange={field.onChange}
                      options={[
                        { value: "", label: "Sem responsável" },
                        ...team.map((member) => ({
                          value: member.id,
                          label: member.name,
                        })),
                      ]}
                    />
                  )}
                />
              </Field>
            </DrawerRow>

            <DrawerRow
              icon={<IconRepeat className="size-4" />}
              label="Repetição"
              open={openRow === "repeat"}
              onToggle={() => setOpenRow((current) => (current === "repeat" ? null : "repeat"))}
              summary={recurrenceDescription ?? "Não repete"}
            >
              <div className="grid gap-3">
                <Field label="Repetição" htmlFor="frequency">
                  <Controller
                    name="frequency"
                    control={control}
                    render={({ field }) => (
                      <MenuSelect
                        id="frequency"
                        aria-label="Repetição"
                        value={field.value}
                        onChange={field.onChange}
                        options={[
                          { value: "none", label: "Não repete" },
                          { value: "DAILY", label: "Diariamente" },
                          { value: "WEEKLY", label: "Semanalmente" },
                          { value: "MONTHLY", label: "Mensalmente" },
                          { value: "YEARLY", label: "Anualmente" },
                        ]}
                      />
                    )}
                  />
                </Field>
                {frequency !== "none" && (
                  <Field
                    label="A cada"
                    htmlFor="interval"
                    hint={recurrenceDescription ?? undefined}
                  >
                    <Input
                      id="interval"
                      type="number"
                      min={1}
                      max={99}
                      {...register("interval", { valueAsNumber: true, min: 1, max: 99 })}
                    />
                  </Field>
                )}
              </div>
            </DrawerRow>

            <DrawerRow
              icon={<IconVideo className="size-4" />}
              label="Videochamada"
              open={openRow === "video"}
              onToggle={() => setOpenRow((current) => (current === "video" ? null : "video"))}
              summary={videoUrl.trim() === "" ? "Sem videochamada" : "Link customizado"}
            >
              <Field
                label="Link da videochamada"
                htmlFor="videoUrl"
                hint="Meet, Zoom ou o link da live. Vira o atalho da pill."
              >
                <Input
                  id="videoUrl"
                  type="url"
                  inputMode="url"
                  placeholder="https://…"
                  {...register("videoUrl")}
                />
              </Field>
            </DrawerRow>

            <DrawerRow
              icon={<IconPublish className="size-4" />}
              label="Publicação"
              open={openRow === "publish"}
              onToggle={() => setOpenRow((current) => (current === "publish" ? null : "publish"))}
              summary={publishDate === "" ? "Sem data de publicação" : publishDate}
            >
              <Field
                label="Data de publicação"
                htmlFor="publishDate"
                hint="Para postagens. Deixe vazio se não se aplica."
              >
                <div className="flex gap-2">
                  <Input id="publishDate" type="date" {...register("publishDate")} />
                  <Input
                    type="time"
                    aria-label="Hora da publicação"
                    className="w-28 shrink-0"
                    {...register("publishTime")}
                  />
                </div>
              </Field>
            </DrawerRow>

            <Field label="Descrição" htmlFor="description">
              <Textarea
                id="description"
                rows={3}
                placeholder="Adicionar notas..."
                {...register("description")}
              />
            </Field>

            <button
              type="button"
              aria-expanded={showAdvanced}
              aria-controls="deliverable-advanced"
              className="flex w-full items-start gap-3 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-3 py-2.5 text-left transition-[background,border-color] duration-[var(--dream-duration-fast)] hover:border-[var(--color-line-strong)] hover:bg-[var(--color-surface-muted)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
              onClick={() => setShowAdvanced((open) => !open)}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[0.8125rem] font-semibold text-[var(--color-ink)]">
                  {showAdvanced ? "Menos opções" : "Mais opções"}
                </span>
                {!showAdvanced && (
                  <span className="mt-0.5 block text-[0.75rem] font-medium text-[var(--color-ink-muted)]">
                    {advancedHint}
                  </span>
                )}
              </span>
              <IconChevronDown
                className={[
                  "mt-0.5 size-4 shrink-0 text-[var(--color-ink-muted)] transition-transform duration-[var(--dream-duration-fast)]",
                  showAdvanced ? "rotate-180" : "",
                ].join(" ")}
              />
            </button>

            {showAdvanced && (
              <div id="deliverable-advanced" className="space-y-4">
                {!isEdit && (
                  <Field label="Status" htmlFor="status">
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <MenuSelect
                          id="status"
                          aria-label="Status"
                          value={field.value}
                          onChange={field.onChange}
                          options={DELIVERABLE_STATUSES.map((item) => ({
                            value: item,
                            label: DELIVERABLE_STATUS_LABELS[item],
                          }))}
                        />
                      )}
                    />
                  </Field>
                )}

                <Field label="Prioridade" htmlFor="priority">
                  <Controller
                    name="priority"
                    control={control}
                    render={({ field }) => (
                      <MenuSelect
                        id="priority"
                        aria-label="Prioridade"
                        value={field.value}
                        onChange={field.onChange}
                        options={DELIVERABLE_PRIORITIES.map((item) => ({
                          value: item,
                          label: DELIVERABLE_PRIORITY_LABELS[item],
                        }))}
                      />
                    )}
                  />
                </Field>

                <fieldset className="min-w-0">
                  <legend className="mb-1.5 block text-[0.8125rem] font-medium text-[var(--color-ink-secondary)]">
                    Links
                  </legend>
                  <p className="mb-2 text-xs text-[var(--color-ink-muted)]">
                    Referências, briefings, Deck Figma de cronograma ou a publicação final. Link do
                    Figma ganha o atalho para o Moon. Links ficam visíveis no portal quando a
                    entrega é visível ao cliente.
                  </p>

                  <div className="space-y-2">
                    {linkFields.fields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          aria-label={`Rótulo do link ${index + 1}`}
                          placeholder="Rótulo (opcional)"
                          className="w-1/3 shrink-0"
                          {...register(`links.${index}.label` as const)}
                        />
                        <Input
                          aria-label={`URL do link ${index + 1}`}
                          type="url"
                          inputMode="url"
                          placeholder="https://..."
                          {...register(`links.${index}.url` as const)}
                        />
                        {isFigmaPresentationUrl(links[index]?.url ?? "") ? (
                          <Link
                            to="/moon"
                            search={{ apresentacao: links[index]?.url.trim() ?? "" }}
                            className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl border border-[var(--color-line-strong)] text-brand-600 hover:bg-[var(--color-surface-muted)]"
                            aria-label="Enviar apresentação ao Moon"
                            title="Enviar ao Moon"
                          >
                            <IconBolt className="size-4" />
                          </Link>
                        ) : null}
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remover link ${index + 1}`}
                          onClick={() => linkFields.remove(index)}
                        >
                          <IconClose className="size-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {linkFields.fields.length < 20 && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-2"
                      onClick={() => linkFields.append({ label: "", url: "" })}
                    >
                      <IconPlus className="size-4" />
                      Adicionar link
                    </Button>
                  )}
                </fieldset>

                <label className="flex cursor-pointer items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-surface-subtle)] p-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 shrink-0 rounded border-[var(--color-line-strong)] text-brand-600"
                    {...register("inviteClient")}
                  />
                  <span className="text-[0.8125rem] leading-5">
                    <span className="font-medium text-[var(--color-ink)]">
                      Convidar o e-mail do cliente
                    </span>
                    <span className="block text-[var(--color-ink-muted)]">
                      {(() => {
                        const emails =
                          selectedClient === undefined ? [] : clientInviteEmails(selectedClient);
                        if (emails.length === 0) {
                          return "Cadastre os e-mails da equipe em Clientes. Sem e-mail com convite ligado, o convite não sai.";
                        }
                        if (emails.length === 1) {
                          return `Google Agenda manda o convite para ${emails[0]}.`;
                        }
                        return `Google Agenda manda o convite para ${emails.length} e-mails: ${formatInviteEmails(emails)}.`;
                      })()}
                    </span>
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-surface-subtle)] p-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 shrink-0 rounded border-[var(--color-line-strong)] text-brand-600"
                    {...register("inviteTeam")}
                  />
                  <span className="text-[0.8125rem] leading-5">
                    <span className="font-medium text-[var(--color-ink)]">
                      Convidar a equipe inteira
                    </span>
                    <span className="block text-[var(--color-ink-muted)]">
                      {team.length === 0
                        ? "Ninguém na equipe ainda."
                        : `Manda o convite para ${team.length} e-mail${team.length === 1 ? "" : "s"} @${team[0]?.email?.split("@")[1] ?? "dreamvision.com.br"}.`}
                    </span>
                  </span>
                </label>

                {isEdit && (
                  <div className="flex items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--color-line)] px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-[0.8125rem] font-medium text-[var(--color-ink)]">
                        Duplicar esta entrega
                      </p>
                      <p className="text-[0.75rem] text-[var(--color-ink-muted)]">
                        A cópia nasce como Planejado.
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => duplicateMutation.mutate()}
                      loading={duplicateMutation.isPending}
                    >
                      Duplicar
                    </Button>
                  </div>
                )}

                {isEdit && activity.data !== undefined && activity.data.length > 0 && (
                  <div className="border-t border-[var(--color-line)] pt-4">
                    <h3 className="mb-2 text-[0.75rem] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
                      Histórico
                    </h3>
                    <ul className="space-y-1.5">
                      {activity.data.slice(0, 8).map((entry) => (
                        <li
                          key={entry.id}
                          className="flex items-baseline gap-2 text-[0.75rem] text-[var(--color-ink-muted)]"
                        >
                          <span className="shrink-0 tabular-nums">
                            {DateTime.fromISO(String(entry.createdAt))
                              .setZone(timezone)
                              .toFormat("dd/MM HH:mm")}
                          </span>
                          <span className="text-[var(--color-ink-secondary)]">
                            {ACTIVITY_LABELS[entry.action] ?? entry.action}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {isEdit && detail.data !== undefined && (
                  <div className="flex items-center gap-2 border-t border-[var(--color-line)] pt-3 text-[0.75rem] text-[var(--color-ink-subtle)]">
                    <StatusBadge status={detail.data.deliverable.status} />
                    <span>versão {version}</span>
                  </div>
                )}
              </div>
            )}
          </form>
        )}
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Excluir entrega"
        message={
          isRecurringSeries
            ? "Isto exclui a série inteira, com todas as suas ocorrências. Para remover apenas uma data, use 'Cancelar esta ocorrência'."
            : "Esta entrega será removida do calendário. O histórico de alterações é preservado para auditoria."
        }
        confirmLabel="Excluir"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />

      <ConfirmDialog
        open={confirmCancelOccurrence}
        onOpenChange={setConfirmCancelOccurrence}
        title="Cancelar esta ocorrência"
        message="Apenas esta data sai do calendário. A regra de recorrência e as demais ocorrências permanecem intactas."
        confirmLabel="Cancelar ocorrência"
        destructive
        loading={cancelOccurrenceMutation.isPending}
        onConfirm={() => cancelOccurrenceMutation.mutate()}
      />
    </>
  );
}

function DateQuickPicks({
  timezone,
  value,
  onPick,
}: {
  timezone: string;
  value: string;
  onPick: (date: string) => void;
}) {
  const today = DateTime.now().setZone(timezone).startOf("day");
  const options = [
    { label: "Hoje", date: today },
    { label: "Amanhã", date: today.plus({ days: 1 }) },
    { label: "Segunda", date: nextWeekdayDate(timezone, 1, today) },
  ];
  return (
    <fieldset className="m-0 flex min-w-0 flex-wrap gap-1.5 border-0 p-0">
      <legend className="sr-only">Atalhos de data</legend>
      {options.map((option) => {
        const iso = option.date.toFormat("yyyy-MM-dd");
        const selected = value === iso;
        return (
          <button
            key={option.label}
            type="button"
            onClick={() => onPick(iso)}
            aria-pressed={selected}
            className={[
              "h-8 rounded-full border px-3 text-[12px] font-semibold transition-colors",
              selected
                ? "border-[#0074FF]/35 bg-brand-50 text-[#0074FF]"
                : "border-[var(--color-line)] text-[var(--color-ink-secondary)] hover:bg-[var(--color-surface-muted)]",
            ].join(" ")}
          >
            {option.label}
          </button>
        );
      })}
    </fieldset>
  );
}

function DrawerRow({
  icon,
  label,
  summary,
  open,
  onToggle,
  children,
}: {
  icon: ReactNode;
  label: string;
  summary?: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-lg px-1 py-2.5 text-left transition-colors hover:bg-[var(--color-surface-subtle)]"
      >
        <span className="text-[var(--color-ink-muted)]">{icon}</span>
        <span className="min-w-0 flex-1 text-[14px] font-semibold text-[var(--color-ink)]">
          {label}
        </span>
        {!open && summary !== undefined && (
          <span className="max-w-[9rem] truncate text-[12px] font-medium text-[var(--color-ink-muted)]">
            {summary}
          </span>
        )}
        <IconChevronDown
          className={[
            "size-4 shrink-0 text-[var(--color-ink-muted)] transition-transform duration-[var(--dream-duration-fast)]",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>
      {open && <div className="dream-mini-window dream-popover-in mb-2 p-3">{children}</div>}
    </div>
  );
}
